export const meta = {
  id: '36-dragons',
  order: 36,
  titleFi: 'Lohikäärmeet',
  titleEn: 'Dragons',
  duolingoUnit: 'duo-42-dragons',
  lemmaIds: ['arraks', 'karakses', 'meles', 'merakses', 'syraks', 'vermaks', 'vagar', 'ēdrurzys', 'vēsperzys', 'drakarys', 'sōvegon', 'zaldrīzāeksio', 'hepagon', 'iōdegon', 'dijemion', 'urnerys', 'elēnagon', 'kipagon', 'pykagon', 'drakarys-2', 'ours:jemvos', 'ours:kipvos', 'ours:visnon', 'ours:kydȳbilla', 'ours:metri', 'ours:kilometeri', 'ours:kilogrami', 'ours:sentimetri', 'ours:maili', 'ours:roketi'],
  exampleIds: [
    'hotd-s1e10-003',
    'hotd-s1e01-024',
    'hotd-s1e02-008',
    'ex-0d93a1398df8',
    'got-s3e04-011',
    'hotd-s1e01-022',
    'ex-45bc089645fe',
    'ex-09de54999c16',
    'ex-725698eacaf9',
    'ex-1786068063f0',
    'ex-c2fecb4758ae',
    'ex-e977d48f3dc1',
    'ex-def4b4de6499',
    'ex-6032c7ba51f4',
    'hotd-s1e10-016',
    'ex-083c392857e8',
    'hotd-s1e08-002',
    'hotd-s1e02-002',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Arraks',
    'https://wiki.languageinvention.com/index.php?title=Merakses',
    'https://wiki.languageinvention.com/index.php?title=Ēdrurzys',
    'https://wiki.languageinvention.com/index.php?title=Vēsperzys',
    'https://wiki.languageinvention.com/index.php?title=Drakarys',
    'https://wiki.languageinvention.com/index.php?title=Pykagon',
    'https://wiki.languageinvention.com/index.php?title=Zaldrīzāeksio',
    'https://wiki.languageinvention.com/index.php?title=Urnerys',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  showLines: ['hotd-s1e10-003', 'hotd-s1e01-024', 'hotd-s1e02-008', 'got-s3e04-011', 'hotd-s1e01-022', 'hotd-s1e10-016', 'hotd-s1e08-002', 'hotd-s1e02-002'],
  pronunciation: { focus: 'z', words: ['Ēdrurzys', 'Vēsperzys', 'zaldrīzāeksio', 'rizmon', 'jorrāeliarza'], lines: ['got-s3e04-011'] },
  phrases: ['drakarys-2'],
  letter: {
    fi: 'Kuvaile oma lohikäärmeesi: nimi, väri, koko ja ratsastaja.',
    en: 'Describe your own dragon: name, colour, size and rider.',
  },
  scene: 'hotd-s1e10-1',
}

# Lohikäärmeet

Kirjasarjan alussa lohikäärmeitä pidetään sukupuuttoon kuolleina, kunnes Daenerys kuoriuttaa kolme munaa, jotka hän sai häälahjaksi. Tällä oppitunnilla opit lohikäärmeiden nimet, niiden hieman oikukkaan taivutuksen ja sanat, joilla lohikäärmeet lentävät, sukeltavat ja syöksevät tulta. Lohikäärmeille huudetut komennot ovat sarjojen valyriaa parhaimmillaan: lyhyitä ja käskeviä.

## Lohikäärmeiden nimet

Englanninkielisissä kirjoissa lohikäärmeiden nimet on kirjoitettu englannin oikeinkirjoituksella, ja valyrian muoto poikkeaa niistä usein. Duolingon tehtävissä kysytään englanninkielisiä kirjoitusasuja, joten opettele parit:

- <Hv glossed>Arraks</Hv> = Arrax
- <Hv glossed>Karakses</Hv> = Caraxes
- <Hv l="meles" glossed>Meles</Hv> = Meleys
- <Hv glossed>Merakses</Hv> = Meraxes
- <Hv l="syraks" glossed>Syraks</Hv> = Syrax
- <Hv l="vermaks" glossed>Vermaks</Hv> = Vermax
- <Hv glossed>Vagar</Hv> = Vhagar

Kaksi englanninkielistä nimeä on käännöksiä eikä valyrialaisia nimiä:

- <Hv glossed>Ēdrurzys</Hv> = Dreamfyre
- <Hv glossed>Vēsperzys</Hv> = Sunfyre

Niiden rakenne selviää sanakirjasta. <Hv>Vēsperzys</Hv> on yhdyssana sanoista <Hv glossed>vēzos</Hv> ’aurinko’ ja <Hv glossed>perzys</Hv> ’tuli’. <Hv>Ēdrurzys</Hv> on sulautuma sanoista <Hv glossed>ēdrurys</Hv> ’uni’ ja <Hv>perzys</Hv>. Englannin *-fyre* on siis valyrian *-perzys*.

<Pronounce word="vēsperzys" />
<Pronounce word="karakses" />

Listasta näkyy, että englannin *x* vastaa näissä nimissä valyrian *ks*:ää, aivan kuten suomessa kirjoitetaan *taksi* eikä *taxi*.

## Nimet, jotka päättyvät -ks

Ks-loppuiset nimet (<Hv>Arraks</Hv>, <Hv l="syraks">Syraks</Hv>, <Hv l="vermaks">Vermaks</Hv>) taipuvat hieman eri tavalla kuin muut. Niiden *s* esiintyy vain nominatiivissa, akkusatiivissa, vokatiivissa, instrumentaalissa ja komitatiivissa. Muista sijoista se puuttuu:

<Paradigm lemma="arraks" numbers="sg" />

Genetiivi on siis <Hv>Arrako</Hv>, mutta instrumentaali <Hv>Arrakso</Hv>. Vertaa nimeen <Hv>Merakses</Hv>, joka taipuu tavallisen sanan tapaan ja säilyttää *ks*:n kaikkialla: genetiivi <Hv>Merakso</Hv>, instrumentaali <Hv>Meraksose</Hv>.

<Paradigm lemma="merakses" numbers="sg" />

Tips & Notes myöntää, että tämä on hieman sekavaa, mutta päätteillä on väliä. Tässä suomi auttaa yllättävän hyvin: suomessakin on sanoja, joiden nominatiivin *s* katoaa muista sijoista. *Kirves*, mutta *kirveen*; *kaunis*, mutta *kauniin*.

<Compare fi="kirvee|n" hv="Arrak|o" label="genetiivi: nominatiivin s katoaa" />

Vokatiivi on lohikäärmeille tärkein sija, koska niitä komennetaan. Ks-nimien vokatiivi on sama kuin nominatiivi:

<Line id="hotd-s1e10-003" />

Caraxesin valyriankielinen nimi <Hv>Karakses</Hv> taas päättyy *-es*, ja sen vokatiivi on <Hv>Karaksys</Hv>. Lohikäärmeenvartijat komentavat sitä näin:

<Line id="hotd-s1e01-024" />

Kaksi *fyre*-nimeä taipuu aurinkosukuisen y-loppuisen sanan tapaan: <Hv glossed>Ēdrurzo</Hv> ’Dreamfyren’, <Hv glossed>Vēsperzomy</Hv> ’Sunfyren kanssa’.

<Line id="hotd-s1e02-008" />

<Example id="ex-0d93a1398df8" />

## Kuuluisat nimet

Lohikäärmeiden lisäksi Duolingon tämän osion tehtävissä on kirjojen kuuluisia henkilönnimiä. Tips & Notes varoittaa kahdesta asiasta. Samaa nimeä voi kantaa useampi henkilö: tämän osion Viserys on eri henkilö kuin *Game of Thronesin* ensimmäisen kauden Viserys. Lisäksi jotkin nimet muistuttavat toisiaan: Rhaenyra ja Rhaenys ovat eri ihmisiä.

Valyriassa ne myös taipuvat eri tavoin. <Hv>Rhaenyra</Hv> on kuusukuinen a-loppuinen nimi, jonka vokatiivi on <Hv>Rhaenyrus</Hv>; sen kuulet Daemonin suusta monta kertaa. <Hv>Rhaenys</Hv> on aurinkosukuinen, ja sen vokatiivi on <Hv>Rhaenys</Hv>.

## Lohikäärmeentuli: drakarys

Kun lohikäärmettä käsketään syöksemään tulta, käytetään sanaa <Hv glossed>drakarys</Hv> ’lohikäärmeentuli’ vokatiivissa. Tällä sanalla vokatiivi on sama kuin nominatiivi. Kuuluisa komento on siis kirjaimellisesti puhuttelu, ikään kuin huudettaisiin itse tulelle: ”Lohikäärmeentuli!”

<Line id="got-s3e04-011" />

<Line id="hotd-s1e01-022" />

<Pronounce l="drakarys" word="drakarys" />

Muissa sijoissa sana taipuu tavallisen aurinkosukuisen y-loppuisen sanan tapaan:

<Paradigm lemma="drakarys" numbers="sg" />

Tulen syökseminen ilmaistaan verbillä <Hv glossed>pykagon</Hv>, jonka perusmerkitys on ’sylkeä’. Sanakirjan mukaan se tarkoittaa yhdessä akkusatiivin <Hv glossed>drakari</Hv> kanssa ’syöstä lohikäärmeentulta’:

<Example id="ex-45bc089645fe" />

Daemon käyttää samaa ilmausta runossa, jossa lohikäärmettä kutsutaan ”tulen syöksijäksi”:

<Example id="ex-09de54999c16" />

<Note>
**Miksi lohikäärmeentulelle on oma sana?** Peterson kertoo wikin <Hv glossed>Zaldrīzes</Hv>-sivulla, että kirjojen *dracarys* muistutti hänestä liikaa kreikan sanaa, josta englannin *dragon* on peräisin. Se oli hänen mukaansa ”joko yksi biljoonasta -sattuma tai laiskuutta”. Siksi lohikäärme on valyriaksi <Hv>zaldrīzes</Hv>, ja <Hv l="drakarys">drakarys</Hv> on nimenomaan lohikäärmeen tulta, eri asia kuin tavallinen tuli <Hv glossed>perzys</Hv>. Mallina oli sarjan villituli, joka sekin on omanlaistaan tulta. Petersonin selityksen nosti esiin jan Misali (Conlang Critic).
</Note>

## Lohikäärmeen verbit

Lohikäärme lentää (<Hv>sōvegon</Hv>), nousee (<Hv>hepagon</Hv>), sukeltaa (<Hv>iōdegon</Hv>) ja kallistuu kaarteeseen (<Hv>elēnagon</Hv>), ja sillä ratsastetaan (<Hv>kipagon</Hv>). Esimerkkilauseissa kannattaa katsoa, missä sijassa lohikäärme on, sillä kolme eri sijaa kertoo kolmesta eri suhteesta:

**Akkusatiivi: lohikäärme on se, jolla ratsastetaan.**

<Example id="ex-725698eacaf9" />

<Interlinear hv="Dārilaros Rhaenys Melī jēdrot kipt|as." gloss="prinsessa-NOM Rhaenys-NOM Meleys-ACC taivas-DAT ratsastaa-PRF.3SG" fi="Prinsessa Rhaenys ratsasti Meleysillä taivaalle." />

Tässä suomi johtaa harhaan: suomeksi ratsastetaan *hevosella*, adessiivissa. Esimerkissä ratsastettava lohikäärme on akkusatiivissa (<Hv l="meles">Melī</Hv>), suorana objektina.

**Komitatiivi: lohikäärme on kumppani, jonka kanssa toimitaan.**

<Example id="ex-1786068063f0" />

<Example id="ex-e977d48f3dc1" />

**Lokatiivi: ratsastaja on lohikäärmeen päällä.**

<Example id="ex-c2fecb4758ae" />

<Hv>Vagār</Hv> on nimen <Hv>Vagar</Hv> lokatiivi. Lokatiivin pitkä vokaali on tässä ainoa ero nominatiiviin, ja suomalainen kuulee sen helposti.

**Instrumentaali: tuli on väline.**

<Example id="ex-def4b4de6499" />

<Interlinear hv="Vagar zenturlion drakar|omy zālt|as." gloss="Vhagar-NOM majatalo-ACC lohikäärmeentuli-INS polttaa-PRF.3SG" fi="Vhagar poltti majatalon lohikäärmeentulella." />

Lennon aikana komennot ovat imperatiiveja ja vokatiiveja peräkkäin:

<Example id="ex-6032c7ba51f4" />

<Line id="hotd-s1e10-016" />

<Pronounce word="elēnagon" />
<Pronounce word="iōdegon" />

## Ratsastajat ja vartijat

Kolme muuta sanaa on rakennettu tutuista osista, ja sanakirja kertoo, miten:

- <Hv glossed>zaldrīzāeksio</Hv> ’lohikäärmeratsastaja, lohikäärmeherra’ = <Hv glossed>zaldrīzes</Hv> ’lohikäärme’ + <Hv glossed>āeksio</Hv> ’herra, isäntä’
- <Hv glossed>urnerys</Hv> ’vartija, lohikäärmeenvartija’ = <Hv glossed>urnegon</Hv> ’nähdä’ + johdin *-tys*, joka muodostaa tekijännimiä (vrt. suomen *-ja*: näkijä, vartija)
- <Hv glossed>dijemion</Hv> ’lämpökammio (lohikäärmeenmunien hautomiseen)’ = <Hv glossed>dijemagon</Hv> ’lämmittää’ + substantiivijohdin *-ion*

<Pronounce word="zaldrīzāeksio" />

<Example id="ex-083c392857e8" />

<Line id="hotd-s1e08-002" />

<Line id="hotd-s1e02-002" />

Viimeisessä <Hv glossed>urneroti</Hv> on sanan <Hv>urnerys</Hv> monikon genetiivi, koska <Term id="postpositio">postpositio</Term> <Hv glossed>rȳ</Hv> ’alla’ vaatii genetiivin. Monikon genetiivi ja datiivi ovat tässä sanassa samat.

## Harjoituslauseita

<Practice hv="Vermaks Arrakso sōvetas." fi="Vermax lensi Arraxin kanssa." />

<Practice hv="Arrako urnerys ēdrus." fi="Arraxin vartija nukkuu." />

<Practice hv="Vagār sōvetan." fi="Lensin Vhagarin selässä." />

Ensimmäisessä komitatiivi <Hv>Arrakso</Hv> säilyttää *s*:n, toisessa genetiivi <Hv>Arrako</Hv> menettää sen. Kolmas noudattaa lauseen <Hv>Visenia Vagār iōdetas</Hv> lokatiivimallia.

## Hahmossa: lohikäärmeratsastajat

Tästä oppitunnista alkaen Puhutaan-osiossa on myös kohtauksia hahmossa. Kumpikin saa Targaryen-hahmon ja tämän repliikkejä sarjasta: Daemon rauhoittelee Caraxesia ja Rhaenyra käskee Syraxia. Ohjatussa keskustelussa toinen on ratsastaja ja toinen lohikäärme. Vapaassa keskustelussa kaksi ratsastajaa kohtaa toisensa taivaalla. Oppitunnit <LessonLink n={49}>49</LessonLink>–<LessonLink n={51}>51</LessonLink> ovat kenraaliharjoitus Halloweeniin.

<SpeakingStep persona />

## Sanat

<Vocab />

<Check />
